import {
  usePriceDisplayOverhaulTranslations,
  useProductIsInWishlist,
} from '@aboutyou/legacy-shared'
import {
  BrowseLaneTypes,
  RecoSectionContent,
} from '@aboutyou/legacy-tracking-sdk'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useRouter } from 'helpers/navigation'
import { useProductTileImageSizes } from 'helpers/product/ProductTileImageSizeContext'
import { productEntityToTileShape } from 'helpers/product/transform'
import {
  ProductModifiers,
  getProductNameWithBrandAndColors,
} from 'helpers/products'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { useProductClickTracking } from 'helpers/tracking/hooks/trackingCollection'
import { ProductImpression } from 'helpers/tracking/trackerComponents/ElementTracker'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useCallback, useContext, useMemo } from 'react'
import { useProductById } from 'services/utils/context'
import { useToggleWishlistHandler } from 'sharedComponents/BuyBox/utils/wishlist'
import {
  useBasketStore,
  useCampaignStore,
  useFeatureFlags,
  useUserStore,
} from 'store/context'
import Variant from 'store/entities/Product/Variant'
import { ImageType } from 'store/features/imageView'
import { useActiveImageType } from 'store/features/imageView/context'
import { useCurrency } from 'store/features/locale/useCurrency'
import { useLocale } from 'store/features/locale/useLocale'
import {
  useCategoryStore,
  useFilterStore,
  usePageType,
  useSortingStore,
} from 'store/sidecar/ProductStreamContext'
import ProductTile from 'uiComponents/molecules/ProductTile/ProductTile'
import ProductTileSoldOut from 'uiComponents/molecules/ProductTile/ProductTileSoldOut'
import { FallbackSkeleton } from '../../uiComponents/molecules/ProductTile/FallbackSkeleton'
import ProductTileCTAContext from './ProductTileCTAContext'

interface Props {
  productId: string
  preferredImageType?: true | ImageType
  importance?: 'high' | 'low'
  isSimilarProduct?: boolean
  renderedInStream?: boolean
  disableContent?: boolean
  productModifiers?: ProductModifiers
  recoType?: RecoSectionContent | BrowseLaneTypes
  onClickTrack?: () => void
  excludeSponsoredBadge?: boolean
  enableAlternativesButton?: boolean
}

const ProductTileConnected: FC<Props> = ({
  onClickTrack,
  productId,
  renderedInStream,
  preferredImageType,
  disableContent,
  importance,
  productModifiers,
  enableAlternativesButton,
  ...config
}) => {
  const activeImageType = useActiveImageType()
  const isOnWishlist = useProductIsInWishlist({ productId: Number(productId) })
  const selectedSingleSizeFilterId = useSelectedSingleSizeFilterId()
  const product = useProductById(productId)
  const { t } = useTranslation()
  const categoryStore = useCategoryStore()
  const enableProductName = useShowProductName()

  const CTAContext = useContext(ProductTileCTAContext)
  const CTAContent = useMemo(
    () => CTAContext?.createButtonLabel(productId),
    [CTAContext, productId],
  )
  const onCTAClick = useMemo(
    () =>
      CTAContext &&
      (() => {
        CTAContext.onButtonClick(productId)
      }),
    [CTAContext, productId],
  )
  const hasCTA = CTAContext?.showButton(productId)

  const imageAltWithView = product
    ? getProductNameWithBrandAndColors(
        {
          brandName: product.brandName,
          productName: product.name,
          colors: product.colors,
        },
        t,
      )
    : undefined

  const enableSustainabilityIcons = useObserver(() => categoryStore.sustainable)
  const isSecondHand = useObserver(() => categoryStore.secondHand)
  const { isActive: isFeatureActive } = useFeatureFlags()
  const enablePriceDisplayOverhaul = isFeatureActive(
    'enablePriceDisplayOverhaul',
  )

  const tile = useProductEntityToTileShape(productId, {
    ...config,
    selectedSizeFilter: selectedSingleSizeFilterId,
    preferredImageType:
      preferredImageType === true ? activeImageType : preferredImageType,
    inSustainableCategory: enableSustainabilityIcons,
    productModifiers,
    isSecondHand,
  })

  const onClick = useCallback(
    (ev: React.MouseEvent<Element, MouseEvent>) => {
      onClickTrack?.()
      tile?.onClick?.(ev)
    },
    [tile?.onClick, onClickTrack],
  )

  if (!tile) {
    return <FallbackSkeleton />
  }

  return (
    <ProductImpression<HTMLAnchorElement | HTMLElement>
      cachePageImpression={renderedInStream}
      productId={productId}
      recoType={config.recoType}
    >
      {({ ref }) => {
        if (tile?.soldout) {
          return (
            <ProductTileSoldOut
              {...tile!}
              tileRef={ref}
              importance={importance}
              alt={`${imageAltWithView}, ${tile.primaryImageTypeTitle}`}
              wishlisted={isOnWishlist}
              disableContent={disableContent}
              enableProductName={enableProductName}
              enableAlternativesButton={enableAlternativesButton}
            />
          )
        }

        return (
          <ProductTile
            {...tile!}
            onClick={onClick}
            enableProductName={enableProductName}
            importance={importance}
            tileRef={ref}
            wishlisted={isOnWishlist}
            disableContent={disableContent}
            alt={imageAltWithView}
            CTAContent={CTAContent}
            onCTAClick={onCTAClick}
            hasCTA={hasCTA}
            enableSustainabilityIcons={enableSustainabilityIcons}
            enablePriceDisplayOverhaul={enablePriceDisplayOverhaul}
          />
        )
      }}
    </ProductImpression>
  )
}

const useProductEntityToTileShape = (
  productId: string,
  config: {
    selectedSizeFilter: string | null
    preferredImageType?: 'bust' | 'model'
    isSimilarProduct?: boolean
    inSustainableCategory?: boolean
    productModifiers?: ProductModifiers
    recoType?: any
    excludeSponsoredBadge?: boolean
    isSecondHand: boolean
  },
) => {
  const [t] = useTranslation()
  const { locale } = useLocale()
  const currency = useCurrency()
  const campaignStore = useCampaignStore()
  const userStore = useUserStore()
  const basketStore = useBasketStore()
  const { navigateClient } = useRouter()
  const product = useProductById(productId)
  const toggleWishList = useToggleWishlistHandler()
  const sortingViewStore = useSortingStore()
  const { defaultImageWidth, imageWidths, imageSizes } =
    useProductTileImageSizes()

  const { isActive: isFeatureActive } = useFeatureFlags()
  const { badgeDeal: badgeDealTranslation } =
    usePriceDisplayOverhaulTranslations()

  const tileData = useObserver(() => {
    if (!product) {
      return null
    }

    let selectedVariant: Variant | undefined

    const availableVariants = product.variants.filter(
      variant => variant.quantity > 0,
    )
    const availableCategoryShopFilterSizes = availableVariants.flatMap(
      variant => variant.sizes.categoryShopFilterSizes,
    )
    const currentSizeCategoryShopFilterSizes =
      availableCategoryShopFilterSizes.filter(
        categoryShopFilterSize =>
          `${categoryShopFilterSize}` === config.selectedSizeFilter,
      )

    if (currentSizeCategoryShopFilterSizes.length === 1) {
      selectedVariant = product.variants.find(variant => {
        return variant.sizes.categoryShopFilterSizes.includes(
          Number(config.selectedSizeFilter),
        )
      })
    }

    const enableSecondaryLabelPhase1 = isFeatureActive(
      'enableSecondaryLabelHRPhase1',
    )
    const enableSecondaryLabelPhase2 = isFeatureActive(
      'enableSecondaryLabelHRPhase2',
    )
    const enablePriceDisplayOverhaul = isFeatureActive(
      'enablePriceDisplayOverhaul',
    )

    return productEntityToTileShape(product, t, {
      currency,
      locale,
      selectedVariantId: selectedVariant ? selectedVariant.id : null,
      campaignColor: product.campaignBadgeColor,
      toggleWishlistProduct: toggleWishList,
      navigate: navigateClient,
      isSaleCampaign: campaignStore.isSaleCampaign,
      likedBrandIds: userStore.likedBrandIdsSet,
      basketProductIds: basketStore.productIds,
      sortChannel: sortingViewStore.sortChannel,
      defaultImageWidth,
      imageWidths,
      imageSizes,
      features: {
        enableSecondaryLabelPhase1,
        enableSecondaryLabelPhase2,
      },
      showSustainableBadge: false,
      excludeSponsoredBadge: config.excludeSponsoredBadge,
      shouldCombineSaleAndCampaignBadges: enablePriceDisplayOverhaul,
      badgeDealTranslation,
      ...config,
    })
  })

  const trackedProductClick = useProductClickTracking({
    productId,
    onClick: tileData?.onClick,
    recoType: config.recoType,
  })
  if (tileData) {
    tileData.onClick = trackedProductClick
  }

  return tileData
}

export const useShowProductName = () => {
  const categoryStore = useCategoryStore()

  const categoryShouldShowProductName = useObserver(
    () => categoryStore.showProductName,
  )
  const pageType = usePageType()

  if (pageType === 'search') {
    return true
  }
  return categoryShouldShowProductName
}

const ProductTileConnectedWithTracking: FC<
  Props & {
    onClickTrack?: () => void
    disableTracking?: boolean
    indexInSection?: number
    enableAlternativesButton?: boolean
  }
> = ({
  onClickTrack,
  renderedInStream,
  indexInSection,
  disableTracking,
  enableAlternativesButton,
  ...props
}) => (
  <ElementTrackingContextProvider
    renderedInStream={renderedInStream}
    id={Number(props.productId)}
    type="product"
    indexInSection={indexInSection}
    disabled={disableTracking}
  >
    <ProductTileConnected
      onClickTrack={onClickTrack}
      renderedInStream={renderedInStream}
      enableAlternativesButton={enableAlternativesButton}
      {...props}
    />
  </ElementTrackingContextProvider>
)

const useSelectedSingleSizeFilterId = (): string | null => {
  const filterViewStore = useFilterStore()

  const selectedSizeFilters =
    filterViewStore.selectedLooseFilters.categoryShopFilterSizes

  if (!selectedSizeFilters || selectedSizeFilters.length !== 1) {
    return null
  }

  return selectedSizeFilters[0]
}

export default memo(ProductTileConnectedWithTracking)
